import { useEffect, useMemo, useRef, useState } from 'react';
import { useAddOrgOrderNote, useOrgOrderNotes } from '../../features/org-orders/notesApi';
import { toast } from 'react-toastify';

function fmtWhen(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString();
}

export function OrderNotesPanel({ orderId }: { orderId: string }) {
  const { data: notes = [], isLoading } = useOrgOrderNotes(orderId);
  const add = useAddOrgOrderNote(orderId);

  const [text, setText] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);

  // simple auto-scroll to bottom when notes change
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [notes.length]);

  const canSend = useMemo(() => {
    const t = text.trim();
    return t.length > 0 && t.length <= 2000 && !add.isPending;
  }, [text, add.isPending]);

  const onSend = async () => {
    const t = text.trim();
    if (!t) return;
    if (t.length > 2000) {
      toast.error('Note is too long (max 2000 chars).');
      return;
    }

    try {
      await add.mutateAsync(t);
      setText('');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to send note');
    }
  };

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="flex-between" style={{ marginBottom: 8 }}>
        <div>
          <h4 style={{ margin: 0 }}>Notes</h4>
          <div className="muted tiny">Shared with customer</div>
        </div>
      </div>

      {/* Input area with consistent styling */}
      <div className="form-control" style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a note…"
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onSend();
            }}
            disabled={add.isPending}
          />
          <button className="btn btn--primary" onClick={onSend} disabled={!canSend}>
            {add.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>

      {/* Notes list */}
      <div
        ref={listRef}
        style={{
          maxHeight: 260,
          overflow: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: 12,
          background: '#f9fafb',
        }}
      >
        {isLoading ? (
          <div className="muted">Loading notes…</div>
        ) : notes.length === 0 ? (
          <div className="muted">No notes yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {notes.map((n) => {
              const isOrg = n.authorType === 'ORG';
              const who = isOrg ? 'Your team' : 'Customer';
              const author = n.author?.name || n.author?.email || who;

              return (
                <div
                  key={n.id}
                  style={{
                    alignSelf: isOrg ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                  }}
                >
                  <div className="muted tiny" style={{ marginBottom: 4 }}>
                    {author} • {fmtWhen(n.createdAt)}
                  </div>
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: '10px 12px',
                      background: isOrg ? '#eff6ff' : '#fff',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      fontSize: '14px',
                    }}
                  >
                    {n.body}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
